import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import axiosNoAuthInstance from "../../axiosApiNoAuth";
import swal from "sweetalert";
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { useWindowDimensions } from "../../../shared/hooks";
import { Box, CircularProgress, Grid } from "@mui/material";
import {
  smallBusinessSectorTrendsDataAtom,
  smallBusinessSectorAtom,
  firmSizeRangeAtom,
  selectedPlaceAtom,
  selectedSpatialValueAtom,
  spatialParameterAtom,
  selectedSpatialLayerAtom,
  industriesDataAtom,
} from "../../../shared/state/atoms";
import { dashboardFeatureFlags, dashboardRoutes } from "../../../shared/constants";

// Wage distribution colour swatch (same as Establishments & Jobs)
const SEGMENT_COLORS = ['#009640', '#66bb6a', '#84746C', '#F5C242', '#F0A400'];
const SEGMENT_LABELS_WITH_RANGES = (handles) => [
  { segment: "Micro", label: `0\u2013${handles[0] - 1}` },
  { segment: "Small", label: `${handles[0]}\u2013${handles[1] - 1}` },
  { segment: "Medium", label: `${handles[1]}\u2013${handles[2] - 1}` },
  { segment: "Large", label: `${handles[2]}\u2013${handles[3] - 1}` },
  { segment: "V Large", label: `${handles[3]}+` },
];

function SmallBusinessSectorTrends() {
  const { height, width } = useWindowDimensions();
  const sbSectorFlags = dashboardFeatureFlags[dashboardRoutes.SMALL_BUSINESS_SECTOR_TRENDS] || {};
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [selectedSpatialValue] = useAtom(selectedSpatialValueAtom);
  const [spatialParameter] = useAtom(spatialParameterAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [sic7] = useAtom(smallBusinessSectorAtom);
  const [, setIndustries] = useAtom(industriesDataAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);
  const [, setData] = useAtom(smallBusinessSectorTrendsDataAtom);
  const [firstGraphData, setFirstGraphData] = useState(null);
  const [otherData, setOtherData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const handles = firmSizeRange?.smallBusinessSectorTrends?.handles || [10, 50, 250, 1000];

  useEffect(() => {
    async function getData() {
      setIsLoading(true);
      try {
        let response;

        if (spatialParameter !== "error") {
          // For metro municipalities, query by district (metro totals available in district data)
          const isMetro = selectedSpatialLayer?.id === "municipality" && selectedPlace?.category === "A";
          const queryParam = isMetro ? `district=${selectedPlace?.district}` : `${spatialParameter}=${selectedSpatialValue}`;
          response = await axiosNoAuthInstance.get(
            `/api/dashboard-small-business-sector-trends/?${queryParam}&sic7_1d=${Math.abs(Number(sic7))}&handle1=${handles[0]}&handle2=${handles[1]}&handle3=${handles[2]}&handle4=${handles[3]}`
          );
        }

        const mappedResponse = response?.data.map((item) => ({ ...item }));
        if (response) setData(mappedResponse);

        const reducedData = mappedResponse.reduce((acc, curr) => {
          const graph = curr.graph;
          const graph_label = curr.graph_label;
          const y_axis_label = curr.y_axis_label;
          const lineDetails = curr.line_details[0];

          Object.values(lineDetails).forEach((lines) => {
            lines.forEach((line) => {
              if (line.label && line.label.trim() !== "") {
                acc.push({
                  graph,
                  graph_label,
                  y_axis_label,
                  line: {
                    line_type: line.line_type,
                    label: line.label,
                    data: line.data.map((d) => ({
                      year: d.year,
                      value: d.value,
                      abs_value: d.abs_value != null ? d.abs_value : 0,
                    })),
                  },
                });
              }
            });
          });

          return acc;
        }, []);

        const groupedData = Object.values(
          reducedData.reduce((acc, item) => {
            acc[item.graph] = acc[item.graph] || [];
            acc[item.graph].push(item);
            return acc;
          }, {})
        );

        let finalData = [];

        groupedData.forEach((item) => {
          let result = {};

          item.forEach(({ graph_label, y_axis_label, line: { label, data } }) => {
            result.graph_label = graph_label;
            result.data = result.data || [];
            result.y_axis_label = y_axis_label;

            data.forEach(({ year, value }) => {
              let yearData = result.data.find((d) => d.year === year);
              if (!yearData) {
                yearData = { year };
                result.data.push(yearData);
              }
              yearData[label] = value;
            });
          });

          finalData = [...finalData, result];
        });

        setFirstGraphData(finalData[0] || null);
        setOtherData(finalData.slice(1));
        setIsLoading(false);
        setErrorMessage(null);
      } catch (error) {
        if (error?.response?.status === 404 || error?.response == null) {
          setIsLoading(false);
          setErrorMessage("No Data");
        } else {
          swal("Failed", `${error}`, "error", { timer: 4000, buttons: false });
        }
      }
    }

    if (selectedPlace) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpatialValue, spatialParameter, selectedPlace, sic7, handles[0], handles[1], handles[2], handles[3]]);

  useEffect(() => {
    async function getData() {
      try {
        const response = await axiosNoAuthInstance.get(`/api/sic-codes/?nextlevel=1`);
        setIndustries(
          response?.data
            .map((industry) => ({ ...industry }))
            .sort((a, b) => b.code - a.code)
        );
      } catch (e) {
        console.log(e);
      }
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segmentLabels = ["Micro", "Small", "Medium", "Large", "V Large"];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-2 bg-white" style={{ border: "1px solid #ccc" }}>
          <p className="m-0 font-bold">{label}</p>
          {payload.slice().reverse().map((entry) => (
            <div key={entry.name} style={{ color: entry.color, margin: 0 }}>
              {entry.name}: {Math.round(entry.value).toLocaleString()} FTE
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = (chartData, chartWidth, chartHeight, showLegend) => (
    <AreaChart
      data={chartData?.data}
      height={chartHeight}
      width={chartWidth}
      margin={{ top: 10, right: 50, left: showLegend ? 100 : 15, bottom: 10 }}
      animationDuration={1000}
    >
      <XAxis dataKey="year" />
      <YAxis
        domain={[0, "auto"]}
        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
        label={showLegend ? { value: chartData?.y_axis_label, angle: -90, dx: -50 } : { value: chartData?.y_axis_label, angle: -90, dx: -17, fontSize: 12 }}
      />
      <CartesianGrid strokeDasharray="3" vertical={false} />
      {!isLoading && segmentLabels.map((seg, i) => (
        <Area
          key={seg}
          type="linear"
          dataKey={seg}
          stackId="1"
          fill={SEGMENT_COLORS[i]}
          stroke={SEGMENT_COLORS[i]}
          fillOpacity={0.7}
        />
      ))}
      {!sbSectorFlags.disableTooltip && <Tooltip content={<CustomTooltip />} />}
    </AreaChart>
  );

  return (
    <div className="flex">
      <div className="w-full">
        <div className="text-lg px-10 pt-5 font-sans font-bold">
          {selectedSpatialLayer?.name} / {selectedPlace?.name} / <span style={{ color: "#1976d2" }}>Small Business</span>
        </div>
        <div className="text-3xl font-bold px-10 mb-10 font-roboto">Growth by Sector</div>
        {!errorMessage ? (
          <div>
            <div style={{ height: `${height * 0.7}px` }} className="overflow-y-auto remove-overflow-during-print">
              {isLoading && (
                <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
                  <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
                    <CircularProgress color="success" size="3rem" />
                  </Box>
                </div>
              )}
              <Grid container rowSpacing={0} columnSpacing={0}>
                <Grid item xs={12}>
                  <div className="px-10 font-bold flex justify-center">{firstGraphData?.graph_label}</div>
                  {firstGraphData && renderChart(firstGraphData, width * 0.7, height * 0.45, true)}
                  <div className="mt-3 mb-8 flex flex-wrap gap-6" style={{ marginLeft: "140px" }}>
                    {SEGMENT_LABELS_WITH_RANGES(handles).map((seg, i) => (
                      <div className="flex items-center" key={seg.segment}>
                        <div className="w-4 h-4 mr-2" style={{ backgroundColor: SEGMENT_COLORS[i] }} />
                        <span>{seg.segment}: {seg.label}</span>
                      </div>
                    ))}
                  </div>
                </Grid>
                {otherData.map((chart) => (
                  <Grid item xs={4} key={chart.graph_label}>
                    <div className="px-10 font-bold flex justify-center items-end" style={{ height: 90 }}>
                      {chart?.graph_label?.length >= 62 ? chart?.graph_label.substring(0, 62) + " ..." : chart?.graph_label}
                    </div>
                    {renderChart(chart, (width - 400) * 0.3, height * 0.4, false)}
                  </Grid>
                ))}
              </Grid>
            </div>
            <div className="pt-2 flex justify-center hidden-during-print">
              <KeyboardDoubleArrowDownIcon />
            </div>
          </div>
        ) : (
          <div className="p-20 flex justify-center">{errorMessage}</div>
        )}
      </div>
    </div>
  );
}

export default SmallBusinessSectorTrends;
